import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResellerContext = createContext(null);

export const useReseller = () => {
  const context = useContext(ResellerContext);
  if (!context) {
    throw new Error('useReseller must be used within a ResellerProvider');
  }
  return context;
};

export const ResellerProvider = ({ children }) => {
  const [reseller, setReseller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('resellerToken');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchResellerProfile();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchResellerProfile = async () => {
    try {
      const response = await axios.get(`${API}/reseller/me`);
      setReseller(response.data);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Failed to fetch reseller profile:', error);
      localStorage.removeItem('resellerToken');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/reseller/login`, { email, password });
    const { token, reseller: resellerData } = response.data;
    localStorage.setItem('resellerToken', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setReseller(resellerData);
    setIsAuthenticated(true);
    return response.data;
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/reseller/register`, data);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('resellerToken');
    delete axios.defaults.headers.common['Authorization'];
    setReseller(null);
    setIsAuthenticated(false);
  };

  const updateProfile = async (data) => {
    const response = await axios.put(`${API}/reseller/me`, data);
    setReseller({ ...reseller, ...data });
    return response.data;
  };

  const value = {
    reseller,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    updateProfile,
    refreshProfile: fetchResellerProfile
  };

  return (
    <ResellerContext.Provider value={value}>
      {children}
    </ResellerContext.Provider>
  );
};

export default ResellerContext;
